import { PERMISSIONS } from 'api/User/types'
import classNames from 'classnames'
import CommonIcon from 'CommonIcon'
import { InfoElement } from 'components'
import { InfoArrow } from 'components/InfoArrow'
import { CurrencyValue } from 'CurrencyValue'
import type { DetailsData } from 'data/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import RestrictedView from 'RestrictedView'
import { thousandsSeparator } from 'utils'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import useStyles from './useStyles'
import { getCurrencyValue, getPercent } from './utils'

interface Props {
  data: DetailsData
}

function DetailsRetail({ data }: Props) {
  const { t: translate } = useTranslation()
  const classes = useStyles()

  return (
    <Grid container item xs={9}>
      <Grid container item xs={12} className={classes.row1} mt="0.7rem">
        <Grid container item xs={5} alignItems="baseline" display="row">
          <CurrencyValue baseFontSize={1.75} currency={data.currency} value={data.revenueCurrentNECom} />
          <InfoElement className={classes.percent} percent={data.revenuePercentNECom} />
        </Grid>
        <Grid container item xs={7} display="row">
          <Box className={`fm fm-returns ${classes.label}`} alignSelf="baseline" />
          {getCurrencyValue(data.returnsValueCurrentNECom, data.currency, translate)} (
          {getPercent(data.returnsValueShareCurrentNECom, translate)}){' | '}
          {getCurrencyValue(data.revenueWOReturnsCurrentNECom, data.currency, translate)}
        </Grid>
      </Grid>

      <Grid container item xs={5} alignItems="baseline" display="row" mt="0.25rem">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <i className={`fm fm-margin ${classes.label}`} />
          <div className={classes.marginPercent}>{getPercent(data.marginCurrentNECom, translate)}</div>
          <div className={classes.marginValue}>{getCurrencyValue(data.marginValueCurrentNECom, data.currency, translate)}</div>
          <InfoArrow current={data.marginValueCurrentNECom} compared={data.marginValueComparedNECom} />
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row" mt="0.25rem">
        <Box fontWeight="bold" ml="1rem">
          {getCurrencyValue(data.revenuePerStoreCurrentNECom, data.currency, translate)} /
        </Box>
        <i className={classNames('fm fm-shop', classes.label)} />
        <InfoElement className={classes.percent} percent={data.revenuePerStorePercentNECom} />
      </Grid>

      <Grid container item xs={5} display="row">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <CommonIcon className={classNames(classes.label, classes.lastYear)} name="lastYear" />
          <div className={classes.marginPercent}>{getPercent(data.marginComparedNECom, translate)}</div>
          <div className={classes.marginComparedValue}>
            {getCurrencyValue(data.marginValueComparedNECom, data.currency, translate)}
          </div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row">
        <i className={classNames('fm fm-traffic', classes.label)} />
        {thousandsSeparator(data.trafficPerStoreCurrentNECom)}
        <InfoElement className={classes.percent} percent={data.trafficPerStorePercentNECom} />
        <div className={classNames(classes.label, classes.avg)}>m²</div>
        {getCurrencyValue(data.floorSpaceCurrentNECom, 'm²', translate, 0)}
        <InfoElement className={classes.percent} percent={data.floorSpacePercentNECom} />
      </Grid>

      <Grid container item xs={12} className={classes.row3} alignItems="flex-end">
        <Grid container item xs={5} alignItems="baseline">
          <i className={classNames('fm fm-replenishment', classes.label)} />
          <div className={classes.marginPercent}>{getPercent(data.replenishmentOnHandShoesCurrent, translate)}</div>
          <i className={classNames('fm fm-future-replenishment', classes.label)} />
          <div className={classes.marginPercent}>{getPercent(data.replenishmentTargetShoesCurrent, translate)}</div>
        </Grid>
        <Grid container item xs={6} alignItems="baseline">
          <i className={`fm fm-temperature ${classes.label}`} />
          {typeof data.temperatureCurrent === 'number' && <Box fontWeight="700">{data.temperatureCurrent}℃</Box>}
          {typeof data.temperatureCurrent === 'number' && typeof data.temperatureCompared === 'number' && (
            <Box ml="0.25rem" mr="0.25rem">
              |
            </Box>
          )}
          {typeof data.temperatureCompared === 'number' && `${translate('revenue.ly')} ${data.temperatureCompared}℃`}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default DetailsRetail
